/**
 * AdminReconciliationPage — F-009.5 (trigger manuale).
 *
 * Schermata ADMIN per lanciare a mano la riconciliazione Stripe↔wallet e
 * vederne l'esito, senza aspettare il job notturno. Utile per verifica e
 * intervento rapido in caso di webhook persi.
 *
 * Struttura (pattern admin-listino): guard ADMIN + AppLayout + QueryClientProvider
 * esterno; `AdminReconciliationPageInner` interno (esportato per i test isolati).
 */
import { RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { reconciliationQueryClient } from '../lib/query-client';
import { errorMessageFor, type ReconciliationReport } from '../lib/reconciliation-client';
import { useRunReconciliation } from '../hooks/useRunReconciliation';

export function AdminReconciliationPage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={reconciliationQueryClient}>
          <AdminReconciliationPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminReconciliationPageInner() {
  const run = useRunReconciliation();
  const report = run.data;

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Riconciliazione pagamenti</h1>
        <p className="text-muted-foreground">
          Controlla i pagamenti Stripe recenti e recupera eventuali ricariche non
          accreditate (es. webhook persi). Avviene in automatico ogni notte: qui puoi
          forzarla adesso.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Esecuzione manuale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            onClick={() => run.mutate(undefined)}
            disabled={run.isPending}
            className="inline-flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${run.isPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {run.isPending ? 'Esecuzione in corso…' : 'Esegui riconciliazione adesso'}
          </Button>

          {run.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>Errore</AlertTitle>
              <AlertDescription>{errorMessageFor(run.error)}</AlertDescription>
            </Alert>
          ) : null}

          {report ? <ReconciliationReportView report={report} /> : null}
        </CardContent>
      </Card>
    </section>
  );
}

function ReconciliationReportView({ report }: { report: ReconciliationReport }) {
  const recovered = report.recovered > 0;
  const since = formatSince(report.windowFromIso);

  return (
    <div className="space-y-3" data-testid="reconciliation-report" aria-live="polite">
      <Alert variant={recovered ? 'default' : undefined}>
        {recovered ? (
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        ) : (
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        )}
        <AlertTitle>
          {recovered
            ? `${report.recovered} ricariche recuperate`
            : 'Tutto allineato — nessun recupero necessario'}
        </AlertTitle>
        <AlertDescription>
          Pagamenti analizzati dal {since}.
        </AlertDescription>
      </Alert>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Analizzati" value={report.scanned} />
        <Stat label="Già a posto" value={report.matched} />
        <Stat label="Recuperati" value={report.recovered} highlight={recovered} />
        <Stat label="Saltati" value={report.skipped} />
        <Stat label="Errori" value={report.failed} highlight={report.failed > 0} />
      </dl>

      {report.recoveredSessionIds.length > 0 ? (
        <div className="text-sm">
          <p className="font-medium">Sessioni recuperate:</p>
          <ul className="mt-1 list-inside list-disc font-mono text-xs text-muted-foreground">
            {report.recoveredSessionIds.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd
        className={`text-2xl font-semibold ${highlight ? 'text-primary' : ''}`}
        data-testid={`stat-${label}`}
      >
        {value}
      </dd>
    </div>
  );
}

/** Formatta l'ISO della finestra in data/ora locale IT; fallback all'ISO grezzo. */
function formatSince(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
